import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { routes } from "@editor/utils/router";

import twMerge from "@replo/design-system/utils/twMerge";
import {
  ChartLine,
  FlaskConical,
  Funnel,
  Globe,
  PaintBucket,
  Settings,
  ShoppingBag,
  SwatchBook,
} from "lucide-react";
import { generatePath, useLocation } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";

import DashboardMenuItem from "./DashboardMenuItem";

export const DashboardMenuItems = ({
  isSidebarExpanded,
}: {
  isSidebarExpanded: boolean;
}) => {
  // TODO (Sebas, 2023-07-10): I don't know why but removing this hook causes
  // the app to crash saying "Cannot access 'andytownApi' before initialization".
  // Investigate and remove this when solved. REPL-7916
  // Note (Fran, 2023-09-12): I will keep the comment above for now, just in case we remove the
  // usage of this hook in the future.
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId() ?? null;
  const projectId = useCurrentProjectId();

  const location = useLocation();
  const currentPathname = location.pathname;

  let menuItems: {
    title: string;
    path: string | null;
    additionalPath?: string | null;
    href: string | null;
    onClick: () => void;
    startEnhancer: React.ReactNode;
  }[] = [];
  if (projectId) {
    const experimentListPath =
      workspaceId &&
      generatePath(routes.shops.experiments.list, {
        projectId,
      });
    menuItems = [
      {
        title: "Shop Site",
        path: `/shops/${projectId}/site`,
        additionalPath: "/home",
        href: `/shops/${projectId}/site`,
        startEnhancer: <Globe size={16} />,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "shop_site",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
      },
      ...(isFeatureEnabled("reaplo")
        ? [
            {
              title: "Funnels",
              path: `/shops/${projectId}/funnels`,
              href: `/shops/${projectId}/funnels`,
              onClick: () => {
                logEvent("dashboard.nav.click", {
                  tab: "funnels",
                  userId: user?.id ?? null,
                  workspaceId,
                });
              },
              startEnhancer: <Funnel size={16} />,
            },
          ]
        : []),

      {
        title: "Analytics",
        path: `/shops/${projectId}/analytics`,
        href: isFeatureEnabled("analytics-v2-data-model")
          ? `/shops/${projectId}/analytics/home`
          : `/shops/${projectId}/analytics/insights`,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: isFeatureEnabled("analytics-v2-data-model")
              ? "analytics_overview"
              : "analytics_insights",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <ChartLine size={16} />,
      },
      {
        title: "A/B Testing",
        path: experimentListPath,
        href: experimentListPath,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "experiments",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <FlaskConical size={16} />,
      },
      ...(isFeatureEnabled("products-shops-menu-item")
        ? [
            {
              title: "Products",
              path: `/shops/${projectId}/products`,
              href: `/shops/${projectId}/products`,
              onClick: () => {
                logEvent("dashboard.nav.click", {
                  tab: "products",
                  userId: user?.id ?? null,
                  workspaceId,
                });
              },
              startEnhancer: <ShoppingBag size={16} />,
            },
          ]
        : []),
      {
        title: "Settings",
        path: `/shops/${projectId}/settings`,
        href: `/shops/${projectId}/settings`,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "settings",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <Settings size={16} />,
      },
      ...(isFeatureEnabled("reaplo")
        ? [
            {
              title: "Style Extraction",
              path: `/shops/${projectId}/style-extraction`,
              href: `/shops/${projectId}/style-extraction`,
              onClick: () => {
                logEvent("dashboard.nav.click", {
                  tab: "style_extraction",
                  userId: user?.id ?? null,
                  workspaceId,
                });
              },
              startEnhancer: <PaintBucket size={16} />,
            },
            {
              title: "Design Library",
              path: `/shops/${projectId}/design-library`,
              href: `/shops/${projectId}/design-library`,
              onClick: () => {
                logEvent("dashboard.nav.click", {
                  tab: "design_library",
                  userId: user?.id ?? null,
                  workspaceId,
                });
              },
              startEnhancer: <SwatchBook size={16} />,
            },
          ]
        : []),
    ];
  } else if (workspaceId) {
    menuItems = [
      {
        title: "Settings",
        path: `/workspace/${workspaceId}/settings`,
        href: `/workspace/${workspaceId}/settings`,
        onClick: () => {
          logEvent("dashboard.nav.click", {
            tab: "settings",
            userId: user?.id ?? null,
            workspaceId,
          });
        },
        startEnhancer: <Settings size={16} />,
      },
    ];
  }

  const isItemSelected = (itemPath: string) => {
    return currentPathname.startsWith(itemPath);
  };

  const renderMenuItem = (item: any, isSubItem = false) => {
    if (!item) {
      return null;
    }

    const isSelected =
      isItemSelected(item.path) || isItemSelected(item.additionalPath);

    return (
      <React.Fragment key={item.title}>
        <DashboardMenuItem
          item={{
            ...item,
            isSubItem,
            isSelected,
          }}
          isSidebarExpanded={isSidebarExpanded}
        />
      </React.Fragment>
    );
  };

  return (
    <div
      className={twMerge(
        "flex w-full flex-col gap-3",
        !isSidebarExpanded && "items-center",
      )}
    >
      {filterNulls(menuItems).map((item, index) => (
        <div key={index}>{renderMenuItem(item)}</div>
      ))}
    </div>
  );
};
